import { MailOutlined, SearchOutlined } from '@ant-design/icons';
import { Skeleton } from 'antd';
import { iconMapping } from 'common/components/IconMapping/IconMapping';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

// Functional Imports (Keep functionality same to same)
import { setAllEmpListRefetchAction } from 'common/commonRedux/auth/actions';
import { authSlice } from 'common/commonRedux/auth/slice';
import { setEpmListData } from 'common/commonRedux/feature/empListSlice';
import { setFilterData } from 'common/commonRedux/feature/filteringSlice';
import { useGetDataQuery } from 'common/commonRedux/getRTKApi/baseApi';
import { RootState } from 'common/redux/store';

import trackforceLogoTwo from '../trackforce_logo_two.png';
import bottomLogo from "./bottomLogo.png";
import darkLogo from './darkThemeFullPng.png';
import './Sidebar.scss';
const Sidebar: React.FC<{ showOnMobile?: boolean }> = ({ showOnMobile = false }) => {
  const location = useLocation();
  const dispatch: any = useDispatch();
  const { t } = useTranslation();
  const isEnglish = t('dashboard:isEN');

  const { profileData } = useSelector((state: any) => state.auth);
  const { getLayoutCollapsed, tokenData } = profileData || {};
  const { setLayoutCollapsed, setIsMenuClicked } = authSlice.actions;
  const intAccountId = useSelector((state: any) => state.auth?.profileData?.tokenData?.accountid);
  const themeMode = useSelector((state: RootState) => state?.theme?.themeMode);
  const isDark = themeMode === 'dark';

  // 1. Fetching Menu List
  const { data: menuData, isLoading: menuLoading } = useGetDataQuery({
    params: { userId: tokenData?.userid },
    urlObjKey: 'GetMenuList',
  });

  // 2. Fetching Employee List (Restored Functionality)
  const {
    data: empList,
    isLoading: empIsLoading,
    isFetching: empIsFetching,
    isError: empIsError,
    refetch: empRefetch,
  } = useGetDataQuery({
    params: { accountId: intAccountId },
    urlObjKey: 'GetEmployeesByAccountId',
  },
  {
    refetchOnMountOrArgChange: false, // Don't auto-refetch on mount or arg change
  }

);

  const [searchValue, setSearchValue] = useState('');
  const [openKey, setOpenKey] = useState<string | null>(null);
  const SETTINGS_LABELS = ['Access Management', 'Works Schedules', 'Promotions', 'System Support', 'Configuration', 'Settings'];

  // --- RESTORED LOGIC: Sync Employee Data ---
  useEffect(() => {
    dispatch(setEpmListData({
      empList: empList?.employees,
      empListLoading: empIsLoading,
      empListFetching: empIsFetching,
      empListIsError: empIsError,
    }));
    dispatch(setAllEmpListRefetchAction({ CB: () => { empRefetch(); } }));
  }, [empList?.employees?.length, empIsFetching, dispatch]);

  // --- RESTORED LOGIC: Reset Filters on Path Change ---
  useEffect(() => {
    dispatch(setFilterData({ pageNumber: 1, searchValue: undefined }));
  }, [location.pathname, dispatch]);

  const { overviewItems, settingsItems } = useMemo(() => {
    if (!menuData) return { overviewItems: [], settingsItems: [] };
    const filtered = menuData.filter((m: any) =>
      m.strLabel.toLowerCase().includes(searchValue.toLowerCase())
    );
    return {
      overviewItems: filtered.filter((m: any) => !SETTINGS_LABELS.includes(m.strLabel)),
      settingsItems: filtered.filter((m: any) => SETTINGS_LABELS.includes(m.strLabel))
    };
  }, [menuData, searchValue]);

  const handleToggle = (key: string) => {
    setOpenKey(openKey === key ? null : key);
  };

  const handleItemClick = () => {
    dispatch(setIsMenuClicked(false));
  };

  return (
    <aside
      className={`${showOnMobile ? 'block xl:flex relative h-full overflow-x-hidden custom-scrollbar' : 'hidden xl:flex fixed inset-y-0 left-0'} z-[1000] flex-col bg-gradient-to-b from-[#173C68] via-[#123152] to-[#0F2B50] border-r-0 shadow-[6px_0_36px_-10px_rgba(15,43,80,0.4)] transition-all duration-300 ease-in-out ${getLayoutCollapsed ? (showOnMobile ? 'w-[250px] xl:w-[82px]' : 'xl:w-[82px]') : (showOnMobile ? 'w-[250px] xl:w-[244px]' : 'xl:w-[244px]')}`}
    >
      {/* Header: Logo + Collapse Toggle */}
      <div className={`flex items-center ${getLayoutCollapsed ? 'justify-center' : 'justify-between'} px-2 pt-4 pb-4 shrink-0 border-b border-white/10`}>
        {!getLayoutCollapsed ? (
          <Link to="/" className="flex items-center group gap-1">
            <img src={trackforceLogoTwo} alt="Logo" className="h-[20px] object-contain transition-transform duration-200 group-hover:scale-[1.02]" />
            <img src={darkLogo} alt="Logo" className="h-[20px] object-contain transition-transform duration-200 group-hover:scale-[1.02]" />
          </Link>
        ) : (
          <Link to="/" className="hidden xl:flex items-center justify-center rounded-md p-1">
            <img src={trackforceLogoTwo} alt="Logo" className="w-6 h-5 object-contain" />
          </Link>
        )}
        <button
          onClick={() => dispatch(setLayoutCollapsed(!getLayoutCollapsed))}
          aria-label={getLayoutCollapsed ? t('layout:sidebar.expand') : t('layout:sidebar.collapse')}
          className={`${getLayoutCollapsed ? 'hidden' : 'hidden xl:flex'} items-center justify-center w-7 h-7 rounded-full border border-white/15 bg-white/10 text-white hover:bg-[#1B73E8] hover:text-white hover:border-[#1B73E8] hover:shadow-md hover:shadow-blue-500/20 transition-all duration-200`}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Floating Toggle for Collapsed State */}
      {getLayoutCollapsed && (
        <button
          onClick={() => dispatch(setLayoutCollapsed(!getLayoutCollapsed))}
          aria-label={t('layout:sidebar.expand')}
          className="hidden xl:flex absolute top-[18px] -right-3 z-[1001] items-center justify-center w-6 h-6 rounded-full border border-white/15 bg-white/10 text-white hover:bg-[#1B73E8] hover:text-white hover:border-[#1B73E8] shadow-md hover:shadow-blue-500/30 transition-all duration-200"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {/* Search Field */}
      {!getLayoutCollapsed && (
        <div className="px-4 pt-4 pb-2">
          <div className="relative flex items-center border border-white/15 bg-white/10 rounded-lg px-3 py-2 transition-all duration-200 hover:border-white/25 focus-within:border-[#1B73E8] focus-within:ring-2 focus-within:ring-[#1B73E8]/30">
            <SearchOutlined className="mr-2 text-[14px] text-[#CBD5E1]" />
            <input
              type="text"
              placeholder={t('layout:sidebar.searchPlaceholder')}
              className="bg-transparent outline-none text-sm w-full text-white placeholder:text-[#94A3B8]"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            {searchValue && (
              <button
                onClick={() => setSearchValue('')}
                className="text-[11px] ml-1 text-[#94A3B8] hover:text-white"
                aria-label={t('layout:sidebar.clearSearch')}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      {/* Menu List */}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pt-2 custom-scrollbar overflow-x-visible">
        {(menuLoading || !menuData) ? (
          <div className="px-2 py-2">
            <div className="mb-3">
              <Skeleton active paragraph={{ rows: 1, width: '60%' }} title={false} />
            </div>
            <Skeleton active paragraph={{ rows: 6 }} title={false} />
          </div>
        ) : (
          <>
            <Section title={t('layout:sidebar.overview')} show={!getLayoutCollapsed} />
            {overviewItems.map((menu: any) => (
              <MenuItem
                key={menu.intFirstLabelId}
                item={menu}
                isEnglish={isEnglish}
                collapsed={getLayoutCollapsed}
                isOpen={openKey === menu.strTo}
                onToggle={() => handleToggle(menu.strTo)}
                currentPath={location.pathname}
                onItemClick={handleItemClick}
                isDark={isDark}
              />
            ))}
            {settingsItems.length > 0 && (
              <div className={`${getLayoutCollapsed ? 'mx-3' : 'mx-1'} my-3 border-t border-white/10`} />
            )}
            <Section title={t('layout:sidebar.settings')} show={!getLayoutCollapsed} />
            {settingsItems.map((menu: any) => (
              <MenuItem
                key={menu.intFirstLabelId}
                item={menu}
                isEnglish={isEnglish}
                collapsed={getLayoutCollapsed}
                isOpen={openKey === menu.strTo}
                onToggle={() => handleToggle(menu.strTo)}
                currentPath={location.pathname}
                onItemClick={handleItemClick}
                isDark={isDark}
              />
            ))}
            <div className="h-3" />
          </>
        )}
      </div>

      {/* Bottom Contact Card */}
      {/* {!getLayoutCollapsed && (
        <div className="p-3 mt-auto shrink-0">
          <div className="relative overflow-hidden rounded-xl px-4 py-3 border border-blue-100/60 bg-gradient-to-br from-[#E8F0FE] via-[#DDE9FE] to-[#D9E9FF] shadow-sm">
            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full blur-2xl pointer-events-none bg-white/40" />
            <div className="absolute -bottom-8 -left-4 w-16 h-16 bg-[#1B73E8]/10 rounded-full blur-2xl pointer-events-none" />
            <div className='relative w-full flex justify-center items-center'>
              <img src={bottomLogo} className="h-[30px] mx-auto object-contain" alt="" />
            </div>
            <a
              href="mailto:support@trackforce.io"
              className="relative mt-3 flex items-center justify-center gap-1 bg-gradient-to-r from-[#1B73E8] to-[#1565D8] hover:from-[#1565D8] hover:to-[#0F5BC8] text-white py-2 rounded-lg text-[11px] font-semibold shadow-md shadow-blue-500/25 hover:shadow-lg hover:shadow-blue-500/40 transition-all duration-200"
            >
              <MailOutlined /> support@trackforce.io
            </a>
          </div>
        </div>
      )} */}
    </aside>
  );
};

const MenuItem = ({ item, isEnglish, collapsed, isOpen, onToggle, currentPath, onItemClick, isDark }: any) => {
  const hasSub = item.secondMenu && item.secondMenu.length > 0;
  const isDashboard = item.strTo === '/';
  const isActive = isDashboard
    ? currentPath === '/'
    : currentPath.startsWith(item.strTo) && item.strTo !== '#';

  const [hovered, setHovered] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [popupTop, setPopupTop] = useState<number | null>(null);

  useEffect(() => {
    if (!hovered) return;
    const compute = () => {
      const wrap = wrapperRef.current;
      const pop = popupRef.current;
      if (!wrap || !pop) return;
      const wrapRect = wrap.getBoundingClientRect();
      const popHeight = pop.offsetHeight || 200;
      const margin = 8;
      let top = wrapRect.top;
      const maxTop = window.innerHeight - popHeight - margin;
      if (top > maxTop) top = maxTop;
      if (top < margin) top = margin;
      setPopupTop(Math.round(top));
    };
    const id = requestAnimationFrame(() => compute());
    const onResize = () => compute();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', onResize);
    };
  }, [hovered]);

  return (
    <div
      ref={wrapperRef}
      className="mb-0.5 relative text-sm group/menu"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        onClick={hasSub && !collapsed ? onToggle : onItemClick}
        className={`relative flex items-center justify-between ${collapsed ? 'px-2 py-2.5 mx-auto w-11 h-11' : 'px-2.5 py-2'} rounded-lg cursor-pointer transition-all duration-200
          ${isActive
            ? 'bg-[#1B73E8] text-white shadow-md shadow-blue-500/25'
            : 'text-[#E2E8F0] hover:bg-[#1B73E8]/20 hover:text-white'}
        `}
      >
        {/* Active left accent bar */}
        {isActive && !collapsed && (
          <span className="absolute -left-3 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-[#1B73E8]" />
        )}

        <Link
          to={hasSub && !collapsed ? '#' : item.strTo}
          className={`flex items-center text-sm gap-3 ${collapsed ? 'justify-center w-full' : ''} flex-1 min-w-0`}
          onClick={(!hasSub || collapsed) ? onItemClick : undefined}
        >
          <span className={`flex items-center justify-center shrink-0 text-sm transition-colors duration-200
            ${isActive
              ? 'text-white'
              : 'text-[#CBD5E1] group-hover/menu:text-white'}`}
          >
            {iconMapping[item.strIcon]}
          </span>
          {!collapsed && (
            <span className={`text-sm truncate transition-colors duration-200 ${isActive ? 'font-semibold' : 'font-medium'}`}>
              {isEnglish ? item.strLabel : item.strBanglaLabel}
            </span>
          )}
        </Link>
        {!collapsed && hasSub && (
          <span className={`flex items-center justify-center w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${isActive ? 'text-white/90' : 'text-[#CBD5E1]'}`}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}

        {/* Collapsed-state tooltip for items without submenu */}
        {collapsed && !hasSub && hovered && (
          <div className="fixed left-[78px] z-[9999] pointer-events-none" style={{ top: wrapperRef.current ? wrapperRef.current.getBoundingClientRect().top + 8 : undefined }}>
            <div className={`text-white text-xs font-medium px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap ${isDark ? 'bg-[#1E293B] border border-[#2E3138]' : 'bg-slate-900'}`}>
              {isEnglish ? item.strLabel : item.strBanglaLabel}
            </div>
          </div>
        )}
      </div>

      {/* --- COLLAPSED FLYOUT (Hover Popup) --- */}
      {collapsed && hasSub && (
        <div
          ref={popupRef}
          className={`fixed left-[72px] z-[9999] transition-all duration-200 pl-2 ${hovered ? 'visible opacity-100 translate-x-0' : 'invisible opacity-0 -translate-x-1'}`}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={popupTop !== null ? { top: popupTop } : undefined}
        >
          <div className={`shadow-[0_10px_40px_-10px_rgba(15,23,42,0.18)] rounded-xl py-2 min-w-[220px] flex flex-col border ${isDark ? 'bg-[#0B172B] border-[#2E3138]' : 'bg-white border-slate-200'}`}>
            <div className={`px-4 py-2 mb-1 text-sm font-bold text-[#1B73E8] uppercase tracking-[0.08em] border-b ${isDark ? 'border-[#2E3138]' : 'border-slate-100'}`}>
              {isEnglish ? item.strLabel : item.strBanglaLabel}
            </div>
            <div className="flex flex-col gap-0.5 px-2 py-1 max-h-[320px] overflow-y-auto custom-scrollbar">
              {item.secondMenu.map((sub: any) => {
                const isSubActive = currentPath === sub.strTo;
                return (
                  <Link
                    key={sub.intSecondLabelId}
                    to={sub.strTo}
                    onClick={onItemClick}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-150 whitespace-nowrap
                  ${isSubActive
                        ? isDark
                          ? 'bg-[#1B73E8]/15 text-[#1B73E8] font-semibold'
                          : 'bg-blue-50 text-[#1B73E8] font-semibold'
                        : isDark
                          ? 'text-[#CBD5E1] hover:bg-[#1E293B] hover:text-[#1B73E8]'
                          : 'text-[#334155] hover:bg-slate-50 hover:text-[#1B73E8]'}
                `}
                  >
                    <span className={`text-sm shrink-0 ${isSubActive ? 'text-[#1B73E8]' : isDark ? 'text-[#94A3B8]' : 'text-[#334155]'}`}>
                      {iconMapping[sub.strIcon] || <div className="w-4" />}
                    </span>
                    <span>{isEnglish ? sub.strLabel : sub.strBanglaLabel}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* --- ACCORDION SUBMENU --- Always rendered when hasSub.
           Container max-height and item opacity/transform animate on a single shared
           timeline (same duration, no per-item delay) so the rows below the parent
           descend at the exact same speed sub-items become visible. Fully reversible. */}
      {!collapsed && hasSub && (
        <div className={`submenu-collapse ${isOpen ? 'submenu-collapse--open' : ''}`}>
          <div className="ml-[18px] mt-2 mb-1 flex flex-col">
            {item.secondMenu.map((sub: any, index: number) => {
              const isSubActive = currentPath === sub.strTo;
              const isLast = index === item.secondMenu.length - 1;
              return (
                <Link
                  key={sub.intSecondLabelId}
                  to={sub.strTo}
                  onClick={onItemClick}
                  className="submenu-item relative flex items-center group py-0.5"
                >
                  {/* --- Tree Line Connector --- */}
                  <div className="absolute left-0 top-0 bottom-0 w-6">
                    <div
                      className={`absolute left-0 w-[1.5px] ${isSubActive ? 'bg-[#1B73E8]/50' : 'bg-white/15'} ${isLast ? 'h-1/2 top-0' : 'h-full'}`}
                    />
                    <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-full h-[1.5px] ${isSubActive ? 'bg-[#1B73E8]/50' : 'bg-white/15'}`} />

                    {/* Connector endpoint: bare icon when active, dot when inactive */}
                    {isSubActive ? (
                      <span className="absolute right-[-3px] top-1/2 -translate-y-1/2 flex items-center justify-center text-white text-sm leading-none z-10">
                        {iconMapping[sub.strIcon] || <span className="block w-1.5 h-1.5 rounded-full bg-white" />}
                      </span>
                    ) : (
                      <span className="absolute right-[1px] top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-white/30 group-hover:bg-white transition-colors duration-200" />
                    )}
                  </div>

                  {/* --- Item Label --- */}
                  <div
                    className={`relative ml-7 flex-1 px-3 py-1.5 rounded-md transition-all duration-200 flex items-center gap-2
              ${isSubActive
                        ? 'bg-[#1B73E8]/20 text-white font-semibold'
                        : 'text-[#CBD5E1] hover:text-white hover:bg-[#1B73E8]/15'
                      }`}
                  >
                    <span className="text-sm font-medium truncate">
                      {isEnglish ? sub.strLabel : sub.strBanglaLabel}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, show }: { title: string, show: boolean }) => (
  show ? (
    <div className="flex items-center gap-2 px-3 mt-4 mb-2">
      <span className="text-sm font-bold tracking-[0.12em] text-[#94A3B8]">{title}</span>
      <span className="flex-1 h-px bg-gradient-to-r from-white/20 to-transparent" />
    </div>
  ) : null
);

export default Sidebar;